"use client";

import { useEffect, useRef } from 'react';
import { getGroupSubmissionRecovery, type GroupSubmissionState } from '@/lib/quiz/group-submission';
import Button from '@/components/ui/Button';

export default function GroupSubmissionRecovery({ state, onRetry, onRestart, groupUrl, storageUnavailable }: {
  state: GroupSubmissionState;
  onRetry: () => void;
  onRestart?: () => void;
  groupUrl?: string;
  storageUnavailable?: boolean;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const busy = state.phase !== 'failed';
  const recovery = getGroupSubmissionRecovery(state);
  // Completion often starts at the bottom of a long mobile page. Focusing
  // the new heading also scrolls the saving/error feedback into view.
  useEffect(() => { heading.current?.focus(); }, [state.phase]);

  return (
    <main className="min-h-screen bg-linear-to-b from-background to-primary/25 p-4 md:p-8 flex items-center justify-center">
      <section aria-busy={busy} className="w-full max-w-lg rounded-2xl border border-slate-200 bg-background p-6 md:p-8 shadow-sm space-y-5">
        <div role={busy ? 'status' : 'alert'} aria-live={busy ? 'polite' : 'assertive'}>
          <h1 ref={heading} tabIndex={-1} className="text-2xl font-semibold text-foreground outline-none">
            {busy ? (state.phase === 'saving' ? 'Saving your result…' : 'Adding your answers to the group…') : recovery.title}
          </h1>
          <p className="mt-3 text-slate-700">
            {busy ? 'Keep this page open while we finish saving.' : recovery.message}
          </p>
        </div>
        {storageUnavailable && <p role="status" className="text-sm text-slate-700">Reload recovery is unavailable in this tab. Keep this page open.</p>}
        {(busy || recovery.retryable) && (
          <Button
            type="button"
            disabled={busy}
            onClick={onRetry}
            className="w-full px-5 disabled:cursor-wait"
          >
            {busy ? 'Saving…' : state.personalResultUrl ? 'Retry saving to group' : 'Try saving again'}
          </Button>
        )}
        {state.personalResultUrl && !busy && (
          <a href={state.personalResultUrl} className="block text-center font-medium text-[#2a4173] underline underline-offset-4">
            View your personal result
          </a>
        )}
        {!busy && onRestart && <button type="button" onClick={onRestart} className="block mx-auto underline underline-offset-4">Start over</button>}
        {!busy && groupUrl && <a href={groupUrl} className="block text-center underline underline-offset-4">Return to the group</a>}
      </section>
    </main>
  );
}
